import React from "react";
import { useNavigate } from "react-router-dom";
import Input from "../../../components/Atomos/Inputs/Input";
import Dropdown from "../../../components/Atomos/DropDown/Dropdown";
import LegacyButton from "../../../components/Atomos/Buttons/LegacyButton";
import ChevronRight from "../../../components/Atomos/Icons/ChevronRight";
import {
  MEMBER_SEARCH_PROFESSIONAL_TITLE_OPTIONS,
  MEMBER_SEARCH_VALIDATION_FIELDS,
  type MemberSearchCriteria,
} from "../memberSearchQuery";
import { ADVANCED_MEMBER_SEARCH_PATH } from "../../../utils/memberSearchNavigation";

type DropdownOption = {
  id: string;
  name: string;
  legacyId?: string | number;
  shortId?: string | number;
};

type MemberSearchFormProps = {
  criteria: MemberSearchCriteria;
  onChange: (patch: Partial<MemberSearchCriteria>) => void;
  onSearch: () => void;
  showValidation: boolean;
  firms: DropdownOption[];
  regions: DropdownOption[];
  locations: DropdownOption[];
  practiceIndustries: DropdownOption[];
  officeLocations: DropdownOption[];
  officeLocationsLoading?: boolean;
};

const MemberSearchForm: React.FC<MemberSearchFormProps> = ({
  criteria,
  onChange,
  onSearch,
  showValidation,
  firms,
  regions,
  locations,
  practiceIndustries,
  officeLocations,
  officeLocationsLoading = false,
}) => {
  const navigate = useNavigate();

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSearch();
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white text-left">
      <div className="mb-6 flex w-full items-center justify-between gap-4">
        <h2 className="shrink-0 font-decimal text-[26px] font-medium leading-8 text-primary md:text-[32px]">
          Member Search
        </h2>
        <div className="ml-auto shrink-0">
          <LegacyButton
            text="Advanced Search"
            color="accent"
            style="filled"
            size="large"
            content="textIcon"
            icon={<ChevronRight className="h-5 w-5 fill-white" />}
            className="!h-12 !min-h-12 !max-h-12 !w-auto !min-w-[260px] !flex-none shrink-0 !justify-between !px-6 !py-0 !text-base md:!text-lg"
            onClick={() => navigate(ADVANCED_MEMBER_SEARCH_PATH)}
          />
        </div>
      </div>

      <div className="mb-5 grid grid-cols-1 items-start gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="flex flex-col">
          <Input
            title=""
            placeholder="Keyword Search"
            value={criteria.keyword}
            onChange={(event) => onChange({ keyword: event.target.value })}
            error={false}
          />
        </div>

        <div className="flex flex-col">
          <Input
            title=""
            placeholder="Last Name"
            value={criteria.lastName}
            onChange={(event) => onChange({ lastName: event.target.value })}
            error={false}
          />
        </div>

        <div className="flex flex-col">
          <Dropdown
            title=""
            placeholder="All Firms"
            data={firms}
            selectedObjst={criteria.firm?.name}
            seter={(option: DropdownOption) => onChange({ firm: option })}
            getid
            internalSearch
            maxHeight="max-h-120"
          />
        </div>

        <div className="flex flex-col">
          <Dropdown
            title=""
            placeholder="All Regions"
            data={regions}
            selectedObjst={criteria.region?.name}
            seter={(option: DropdownOption) => onChange({ region: option })}
            getid
            internalSearch
            maxHeight="max-h-120"
          />
        </div>

        <div className="flex flex-col">
          <Dropdown
            title=""
            placeholder="All Countries"
            data={locations}
            selectedObjst={criteria.location?.name}
            seter={(option: DropdownOption) => onChange({ location: option })}
            getid
            internalSearch
            maxHeight="max-h-120"
          />
        </div>

        <div className="flex flex-col">
          <Dropdown
            title=""
            placeholder="All Professional Titles"
            data={MEMBER_SEARCH_PROFESSIONAL_TITLE_OPTIONS}
            selectedObjst={criteria.professionalTitle?.name}
            seter={(option: DropdownOption) =>
              onChange({ professionalTitle: option })
            }
            getid
            internalSearch={false}
            maxHeight="max-h-120"
          />
        </div>

        <div className="flex flex-col">
          <Dropdown
            title=""
            placeholder="All Practice Industries"
            data={practiceIndustries}
            selectedObjst={criteria.practiceIndustry?.name}
            seter={(option: DropdownOption) =>
              onChange({ practiceIndustry: option })
            }
            getid
            internalSearch
            maxHeight="max-h-120"
          />
        </div>

        <div className="flex flex-col">
          <Dropdown
            key={criteria.firm?.id ?? "all-firms"}
            title=""
            placeholder={
              officeLocationsLoading
                ? "Loading office locations..."
                : criteria.firm
                  ? "All offices for this firm"
                  : "All Office Locations"
            }
            data={officeLocations}
            selectedObjst={criteria.officeLocation?.name}
            seter={(option: DropdownOption) =>
              onChange({ officeLocation: option })
            }
            getid
            internalSearch
            maxHeight="max-h-120"
            disabled={officeLocationsLoading && officeLocations.length === 0}
          />
        </div>
      </div>

      <div className="mb-5 flex w-full justify-end">
        <LegacyButton
          text="Search"
          onClick={onSearch}
          color="primary"
          style="filled"
          size="large"
          content="textIcon"
          icon={<ChevronRight className="h-5 w-5 fill-white" />}
          className="!h-12 !min-h-12 !max-h-12 !w-auto !min-w-[160px] !flex-none shrink-0 !justify-between !px-6 !py-0 !text-base md:!text-lg"
        />
      </div>

      {showValidation ? (
        <div className="mb-5 rounded-md border border-gray-200 bg-[#F6F6F6] px-5 py-4">
          <p className="mb-3 text-base font-medium text-primary md:text-lg">
            Please enter at least one of the following fields:
          </p>
          <div className="grid grid-cols-1 gap-2 text-base text-primary md:grid-cols-4 md:text-lg">
            {MEMBER_SEARCH_VALIDATION_FIELDS.map((field) => (
              <span key={field}>{field}</span>
            ))}
          </div>
        </div>
      ) : null}
    </form>
  );
};

export default MemberSearchForm;
